/**
 * Foundation · Copy — pianificatore generico "Copia da…" per calendari, turni, pianificazioni.
 * Puro e indipendente dal modulo: il chiamante fornisce lettura origine/destinazione e regole.
 */
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (iso) => new Date(`${iso}T00:00:00`);
const weekday = (iso) => (parse(iso).getDay() + 6) % 7;

export const addDays = (iso, n) => { const d = parse(iso); d.setDate(d.getDate() + n); return toIso(d); };

/** Stesso giorno della settimana e stessa occorrenza (1°, 2°… lunedì) nel mese indicato; fallback all'ultima. */
export function sameWeekdayInMonth(iso, year, month) {
  const wd = weekday(iso);
  const ordinal = Math.floor((parse(iso).getDate() - 1) / 7);
  const last = new Date(year, month, 0).getDate();
  const hits = [];
  for (let day = 1; day <= last; day++) {
    const cand = `${year}-${pad(month)}-${pad(day)}`;
    if (weekday(cand) === wd) hits.push(cand);
  }
  return hits[Math.min(ordinal, hits.length - 1)];
}

export const COPY_MAPPERS = Object.freeze({
  offset: (days) => (iso) => addDays(iso, -days),
  sameWeekdayIn: (year, month) => (iso) => sameWeekdayInMonth(iso, year, month),
  sameDate: () => (iso) => iso,
});

/** Settimane (lunedì-domenica) che contengono le date indicate. */
export function weeksOf(dates) {
  const weeks = [];
  dates.forEach((iso) => {
    if (!weeks.length || weekday(iso) === 0) weeks.push({ index: weeks.length, dates: [] });
    weeks[weeks.length - 1].dates.push(iso);
  });
  return weeks.map((w) => ({ ...w, label: `Settimana ${w.index + 1} (${w.dates[0].slice(8)}–${w.dates[w.dates.length - 1].slice(8)})` }));
}

/** scope: { type: "month" } | { type: "week", week } | { type: "days", dates } */
export function resolveTargets(dates, scope) {
  if (scope?.type === "week") return weeksOf(dates)[scope.week]?.dates || [];
  if (scope?.type === "days") return dates.filter((d) => (scope.dates || []).includes(d));
  return [...dates];
}

/**
 * Azioni: copy (destinazione vuota) · overwrite · kept (già compilata, non sovrascritta) · same · blocked · none (origine vuota).
 * `cascade`: le copie pianificate diventano origine per le date successive (es. giorno precedente a catena).
 */
export function planCopy({ targets, mapDate, readSource, readTarget, isEmpty, isEqual, conflictOf, overwrite = false, cascade = false }) {
  const planned = {};
  const items = [...targets].sort().map((target) => {
    const source = mapDate(target);
    const src = cascade && planned[source] ? planned[source] : readSource(source);
    if (!src || isEmpty(src)) return { target, source, action: "none" };
    const conflict = conflictOf?.(target) || null;
    if (conflict === "blocked") return { target, source, action: "blocked", conflict };
    const tgt = readTarget(target);
    const filled = tgt && !isEmpty(tgt);
    let action = "copy";
    if (filled && isEqual(src, tgt)) action = "same";
    else if (filled) action = overwrite ? "overwrite" : "kept";
    if (action === "copy" || action === "overwrite") planned[target] = src;
    return { target, source, action, conflict, value: src };
  });
  const counts = items.reduce((c, i) => ({ ...c, [i.action]: (c[i.action] || 0) + 1 }), {});
  return { items, counts, changes: items.filter((i) => i.action === "copy" || i.action === "overwrite") };
}
